import React, { useEffect, useState } from 'react';
import './styles';
import { useDispatch, useSelector } from 'react-redux';
import PLEditImage from '../../../../../components/PL/EditImage';
import UIListInputRow from '../../../../../components/UI/UIListInputRow';
import UIButton from '../../../../../components/UI/UIButton/UIButton';
import { getInitialRoute } from '../../../../../utils/Helpers/UserHelpers';
import {
	UI_LABELED_INPUT_BUTTON_TYPE,
	UI_LABELED_INPUT_PHONE_NUMBER_TYPE,
	UI_LABELED_INPUT_TEXT_FIELD,
	UI_LABELED_INPUT_TEXT_TYPE
} from '../../../../../components/UI/UILabeledInput/config';
import Queries from '../../../../../utils/API/Queries';
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/react-hooks';
import { toast } from 'react-toastify';
import { getIsUserAdminSelector } from '../../../../../redux/selectors/CurrentUserSelectors';
import { setSignedInUser, setSuperUserReturnRoute } from '../../../../../redux/store/user/actions';
import { useHistory, useLocation } from 'react-router-dom';
import * as strings from './strings';
import { GET_TECHNICIAN_INFO } from './queries';
import { camelCaseToSentence } from '../../../../../utils/Helpers/StringHelpers';

export default function IndividualTechnicianInfo({ technicianId }: IndividualTechnicianInfoProps) {
	const isUserAdmin = useSelector(getIsUserAdminSelector);

	const [technician, setTechnician] = useState<UserType>();
	const [technicianName, setTechnicianName] = useState<string>();
	const [technicianEmail, setTechnicianEmail] = useState<string>();
	const [technicianPhoneNumber, setTechnicianPhoneNumber] = useState<string>();
	const [technicianBio, setTechnicianBio] = useState<string>();
	const [isActive, setIsActive] = useState<boolean>();

	const [updateTechnicianApi] = useMutation(Queries.UPDATE_USER_MUTATION, {
		onError: (error) => toast.error(strings.errorUpdatingUser(error.message))
	});

	const [getTechnician, { data: technicianData }] = useLazyQuery(GET_TECHNICIAN_INFO, {
		variables: { id: technicianId }
	});

	useEffect(() => {
		(async () => {
			await getTechnician();
		})();
	}, []);

	useEffect(() => {
		(() => {
			if (technicianData) {
				const fetchedTechnician: UserType = technicianData['user'];
				setTechnician(fetchedTechnician);
				setTechnicianName(fetchedTechnician.name);
				setTechnicianEmail(fetchedTechnician.email);
				setTechnicianPhoneNumber(fetchedTechnician.phoneNumber);
				setTechnicianBio(fetchedTechnician.bio);
				setIsActive(fetchedTechnician.isActive);
			}
		})();
	}, [technicianData]);

	const client = useApolloClient();
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();

	if (!technician) return null;

	const updateTechnician = async (key: string, value: any) => {
		const response = await updateTechnicianApi({ variables: { id: technicianId, [key]: value } });
		if (response.data['updateUser']) {
			await getTechnician();
			toast.success(strings.updateUserSuccess(camelCaseToSentence(key)));
		} else {
			toast.error(strings.errorUpdatingUser(`User update failed.`));
		}
	};

	const toggleActive = async () => {
		let checked = !technician.isActive;
		const response = await client.mutate({
			mutation: Queries.UPDATE_USER_MUTATION,
			variables: { id: technician.id, isActive: checked }
		});
		if (response['data'].updateUser) {
			setIsActive(checked);
			toast.success(`${technician.name} has been ${!checked ? 'deactivated' : 'activated'}`);
		}
	};

	const renderViewAsTechnician = () => {
		if (isUserAdmin) {
			const viewAsTechnician = () => {
				dispatch(setSignedInUser(technician));
				dispatch(setSuperUserReturnRoute(location.pathname));
				history.push(getInitialRoute(technician) + location.search);
			};

			return (
				<p className='individualTechnicianInfo--viewAs' onClick={viewAsTechnician}>
					{strings.viewPortalAsTechnician}
				</p>
			);
		}
		return null;
	};

	return (
		<div className='individualTechnicianInfo'>
			<div className='individualTechnicianInfo__editImage'>
				<PLEditImage
					imageStyle={{ width: 128, height: 128 }}
					userId={technician.id}
					userName={technician.name}
					userImage={technician.image}
					updateUser={getTechnician}
				/>
			</div>
			{renderViewAsTechnician()}
			<UIListInputRow
				label={strings.nameLabel}
				type={UI_LABELED_INPUT_TEXT_TYPE}
				savedValue={technician.name}
				onChangeValue={setTechnicianName}
				onSave={() => updateTechnician('name', technicianName)}
				value={technicianName}
				placeholder={strings.namePlaceholder}
			/>
			<UIListInputRow
				label={strings.emailLabel}
				type={UI_LABELED_INPUT_TEXT_TYPE}
				savedValue={technician.email}
				onChangeValue={setTechnicianEmail}
				onSave={() => updateTechnician('email', technicianEmail)}
				value={technicianEmail}
				placeholder={strings.emailPlaceholder}
			/>
			<UIListInputRow
				label={strings.phoneNumberLabel}
				type={UI_LABELED_INPUT_PHONE_NUMBER_TYPE}
				savedValue={technician.phoneNumber}
				onChangeValue={setTechnicianPhoneNumber}
				onSave={() => updateTechnician('phoneNumber', technicianPhoneNumber)}
				value={technicianPhoneNumber}
				placeholder={strings.phoneNumberPlaceholder}
			/>
			<UIListInputRow
				label={strings.bioLabel}
				type={UI_LABELED_INPUT_TEXT_FIELD}
				savedValue={technician.bio}
				onChangeValue={setTechnicianBio}
				onSave={() => updateTechnician('bio', technicianBio)}
				value={technicianBio}
				placeholder={strings.bioPlaceholder}
			/>
			<UIListInputRow
				key={`is-active:${isActive}`}
				label={strings.deactivateLabel}
				value={isActive ? strings.deActivate : strings.reActivate}
				buttonType={isActive ? UIButton.CANCEL_TYPE : UIButton.FILL_TYPE}
				type={UI_LABELED_INPUT_BUTTON_TYPE}
				onClick={toggleActive}
			/>
		</div>
	);
}
